import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Contact,
  Loader2,
  ScrollText,
  Send,
  SlidersHorizontal,
  Bell,
  Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useAuthStore } from '@/lib/auth/store';
import {
  useAllocatedDedicatedClis,
  useComposeCampaign,
  useUpdateCampaign,
  useUploadBaseFile,
  useUploadContactList,
  useUserLocations,
} from '../api';
import { buildComposeRequest, resolveTemplateId } from '../payload';
import {
  DEFAULT_FORM_VALUES,
  IS_CONTINUOUS,
  templateFeatures,
  type VoiceCampaignFormValues,
} from '../types';
import { StepBasics } from './steps/StepBasics';
import { StepContacts } from './steps/StepContacts';
import { StepContent } from './steps/StepContent';
import { StepRouting } from './steps/StepRouting';
import { StepNotifications } from './steps/StepNotifications';
import { StepReview } from './steps/StepReview';

const STEPS: StepDescriptor[] = [
  { id: 'basics', label: 'Basics', icon: Megaphone },
  { id: 'contacts', label: 'Contacts', icon: Contact },
  { id: 'content', label: 'Content', icon: ScrollText },
  { id: 'routing', label: 'Routing', icon: SlidersHorizontal },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'launch', label: 'Schedule & Launch', icon: CalendarClock },
];

interface ComposeWizardProps {
  mode: 'create' | 'update' | 'clone';
  initialValues?: Partial<VoiceCampaignFormValues>;
  /** Required for update; ignored in create/clone. */
  campaignId?: number;
  pageTitle: string;
  pageDescription: string;
  backHref?: string;
}

/** Validate the current step. Return an error message or null. */
function validateStep(
  step: number,
  v: VoiceCampaignFormValues,
  baseFile: File | null,
): string | null {
  const f = templateFeatures(v.templateId);

  if (step === 0) {
    if (!v.campaignName.trim()) return 'Give the campaign a name.';
    return null;
  }

  if (step === 1) {
    if (IS_CONTINUOUS(v.templateId)) return null;
    if (v.baseFileMode === 'contactList' && !v.contactList.trim())
      return 'Paste at least one phone number.';
    if (v.baseFileMode === 'newBaseFile' && !baseFile)
      return 'Pick a file to upload.';
    if (v.baseFileMode === 'newBaseFile' && !v.baseName.trim())
      return 'Give the uploaded list a name.';
    if (v.baseFileMode === 'oldBaseFile' && !v.oldBaseId)
      return 'Pick an existing contact list.';
    return null;
  }

  if (step === 2) {
    // Required voice files
    if (f.requiredVoiceFiles.includes('welcome') && !v.welcomeId)
      return 'Pick a welcome voice file.';
    if (f.requiredVoiceFiles.includes('menu') && !v.menuId)
      return 'Pick a menu voice file.';
    if (f.requiredVoiceFiles.includes('noAgent') && !v.noAgentId)
      return 'Pick a "no agent available" voice file.';

    // Menu wait + re-prompt are required wherever the menu prompt is.
    if (f.needsMenuWait) {
      if (!v.menuWaitTime) return 'Menu wait time is required (1–10 seconds).';
      if (!v.rePrompt && v.rePrompt !== '0')
        return 'Re-prompt count is required (0–3).';
    }

    // DTMF picker — at least one valid key must be selected.
    if (f.hasDtmfPicker && v.dtmf.length === 0)
      return 'Select at least one DTMF digit.';

    // Patch list — at least one agent group must be added.
    if (f.hasPatchList && v.patchList.length === 0)
      return 'Add at least one agent group + DTMF row.';

    // Custom IVR / TTS Flow / TTS rows
    if (f.hasIvr && !v.ivrId) return 'Pick a Custom IVR flow.';
    if (f.hasTtsFlow && !v.selectedTTSFlowId)
      return 'Pick a TTS flow.';
    if (f.hasTts) {
      if (!v.language) return 'Pick a language for TTS.';
      if (!v.gender) return 'Pick a voice gender for TTS.';
      if (v.ttsRows.length === 0)
        return 'Add at least one TTS row (Prompt or Variable).';
    }

    return null;
  }

  if (step === 3) {
    // Location is required for every campaign type.
    if (v.locationIds.length === 0)
      return 'Select at least one location.';
    return null;
  }

  // Schedule validation runs as part of the final submit.
  return null;
}

export function ComposeWizard({
  mode,
  initialValues,
  campaignId,
  pageTitle,
  pageDescription,
  backHref = '/voice-campaign-summary',
}: ComposeWizardProps) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const baseFileRef = useRef<HTMLInputElement>(null);

  const [values, setValuesRaw] = useState<VoiceCampaignFormValues>(() => ({
    ...DEFAULT_FORM_VALUES,
    ...initialValues,
  }));

  const setValues = (patch: Partial<VoiceCampaignFormValues>) =>
    setValuesRaw((prev) => ({ ...prev, ...patch }));

  const stepper = useStepper(STEPS.length);

  const uploadContacts = useUploadContactList();
  const uploadFile = useUploadBaseFile();
  const compose = useComposeCampaign();
  const update = useUpdateCampaign();
  // Denormalize locationName + cli value into the compose payload —
  // backend expects objects (`{locationId, locationName}`, `{cliId, cli}`)
  // not bare ids, otherwise it throws "JSONArray[0] is not a JSONObject".
  const userLocations = useUserLocations();
  const userClis = useAllocatedDedicatedClis();
  const locationNameById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const loc of userLocations.data ?? []) {
      out[String(loc.locationId)] = loc.locationName;
    }
    return out;
  }, [userLocations.data]);
  const cliNameById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of userClis.data ?? []) {
      out[String(c.cliId)] = c.cli;
    }
    return out;
  }, [userClis.data]);

  const isPending =
    uploadContacts.isPending ||
    uploadFile.isPending ||
    compose.isPending ||
    update.isPending;

  const handleNext = () => {
    const file = baseFileRef.current?.files?.[0] ?? null;
    const err = validateStep(stepper.current, values, file);
    if (err) {
      toast.error(err);
      return;
    }
    stepper.next();
  };

  const resolveBaseId = async (): Promise<number | null> => {
    if (IS_CONTINUOUS(values.templateId)) return 0;
    const templateId = resolveTemplateId(values);
    if (values.baseFileMode === 'oldBaseFile') {
      const id = Number(values.oldBaseId);
      return Number.isFinite(id) ? id : null;
    }
    if (values.baseFileMode === 'contactList') {
      const res = await uploadContacts.mutateAsync({
        userId: Number(userId),
        templateId,
        contactList: values.contactList,
      });
      return res.baseId ?? null;
    }
    const file = baseFileRef.current?.files?.[0];
    if (!file) return null;
    const res = await uploadFile.mutateAsync({
      userId: Number(userId),
      templateId,
      baseFile: file,
      baseName: values.baseName.trim(),
      contactList: '',
    });
    return res.baseId ?? null;
  };

  const handleSubmit = async () => {
    if (values.sendLater) {
      if (!values.scheduleDate) {
        toast.error('Pick a date to schedule the campaign.');
        return;
      }
      if (new Date(values.scheduleDate) < new Date()) {
        toast.error('Scheduled time must be in the future.');
        return;
      }
    }
    try {
      const baseId = await resolveBaseId();
      if (baseId === null) {
        toast.error('Could not resolve a contact list.');
        return;
      }
      const req = buildComposeRequest({
        userId: Number(userId),
        values,
        baseId,
        locationNameById,
        cliNameById,
      });

      const res =
        mode === 'update' && campaignId
          ? await update.mutateAsync({ ...req, campaignId })
          : await compose.mutateAsync(req);

      if (res?.message === 'Parameters Incorrect') {
        toast.error(res.message);
        return;
      }
      toast.success(
        res?.message ??
          (mode === 'update' ? 'Campaign updated' : 'Campaign submitted'),
      );
      navigate(backHref);
    } catch {
      toast.error('Could not submit the campaign.');
    }
  };

  const stepBody = useMemo(() => {
    switch (stepper.current) {
      case 0:
        return (
          <StepBasics
            values={values}
            setValues={setValues}
            lockTemplate={mode === 'update'}
          />
        );
      case 1:
        return (
          <StepContacts
            values={values}
            setValues={setValues}
            baseFileRef={baseFileRef}
          />
        );
      case 2:
        return <StepContent values={values} setValues={setValues} />;
      case 3:
        return <StepRouting values={values} setValues={setValues} />;
      case 4:
        return <StepNotifications values={values} setValues={setValues} />;
      case 5:
        return <StepReview values={values} setValues={setValues} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepper.current, values, mode]);

  return (
    <ScrollPage>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          <Button asChild variant="outline">
            <Link to={backHref}>
              <ArrowLeft className="h-4 w-4" />
              Back to campaigns
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={STEPS}
        current={stepper.current}
        completed={stepper.completed}
        onStepClick={stepper.goTo}
      />

      <div className="mt-6 space-y-4">{stepBody}</div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={stepper.prev}
          disabled={stepper.isFirst || isPending}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {stepper.isLast ? (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {mode === 'update' ? 'Save changes' : 'Submit campaign'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={isPending}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ScrollPage>
  );
}
